import { Component, computed, effect, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { filter, take, tap } from 'rxjs';
import { FormAvatarComponent, FormInputComponent, FormPasswordComponent, FormSelectComponent, FormTextareaComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { UserFacade } from '@app/store/user';
import { FormOptionsFacade, injectOrganisationOptions, injectSchoolOptions } from '@app/store/form-options';
import { CountryCode, COUNTRY_OPTIONS, getUserRoleOptions, UserRole } from '@shared/types';
import { toScopeValue, updateScopeValidators } from '../user-scope';
import { AccessButtonComponent } from './access-button/access-button.component';

@Component({
    selector: 'app-user-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        DatePipe,
        ButtonModule,
        TagModule,
        FormAvatarComponent,
        FormInputComponent,
        FormPasswordComponent,
        FormSelectComponent,
        FormTextareaComponent,
        UnavailableComponent,
        AccessButtonComponent,
    ],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly userFacade = inject(UserFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly formOptionsFacade = inject(FormOptionsFacade);

    readonly user = this.userFacade.user;
    readonly loading = this.userFacade.loading;
    readonly saving = this.userFacade.saving;
    readonly error = this.userFacade.error;

    readonly roleOptions = getUserRoleOptions();
    readonly countryOptions = COUNTRY_OPTIONS;
    readonly schoolOptions = injectSchoolOptions();
    readonly organisationOptions = injectOrganisationOptions();

    readonly form = this.formBuilder.group({
        firstName: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        lastName: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        email: this.formBuilder.control('', [FormValidators.Required, FormValidators.Email]),
        role: this.formBuilder.control<UserRole>('teacher'),
        description: this.formBuilder.control(''),
        country: this.formBuilder.control<CountryCode | null>(null, [FormValidators.Required]),
        schoolId: this.formBuilder.control<string | null>(null, [FormValidators.Required]),
        organisationId: this.formBuilder.control<string | null>(null),
        avatarId: this.formBuilder.control<string | null>(null),
        contactEmail: this.formBuilder.control('', [FormValidators.Email]),
        password: this.formBuilder.control('', [FormValidators.Password({ minLength: 8, lowercaseLetters: 1, uppercaseLetters: 1, numbers: 1, symbols: 1 })]),
    });

    private readonly roleValue = toSignal(this.form.controls.role.valueChanges, { initialValue: this.form.controls.role.value });
    readonly isSchoolAdmin = computed(() => this.roleValue() === 'admin');
    readonly isOrganisationAdmin = computed(() => this.roleValue() === 'organisation_admin');

    private readonly emailValue = toSignal(this.form.controls.email.valueChanges, { initialValue: this.form.controls.email.value });
    readonly emailEdited = computed(() => this.emailValue().trim() !== (this.user()?.email ?? ''));

    constructor() {
        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.userFacade.getUser(id);
        }

        this.userFacade.user$
            .pipe(
                filter(Boolean),
                take(1),
                tap((user) => {
                    this.form.patchValue({
                        ...user,
                        avatarId: user.avatarId ? String(user.avatarId) : null,
                        contactEmail: user.contactEmail ?? '',
                    });
                }),
                takeUntilDestroyed(),
            )
            .subscribe();

        effect(() => {
            updateScopeValidators(this.form.controls, this.isOrganisationAdmin());
        });

        effect(() => {
            if (this.userFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.userFacade.saving() === false && this.userFacade.saved() === false) {
                this.form.enable();
            }
        });
    }

    ngOnDestroy(): void {
        this.userFacade.clear();
        this.formOptionsFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const user = this.user();
        if (this.form.valid && user) {
            this.form.disable();
            const value = this.form.getRawValue();
            this.userFacade.updateUser(user.id, {
                ...value,
                ...toScopeValue(value, this.isOrganisationAdmin()),
                contactEmail: value.role === 'admin' ? value.contactEmail || null : null,
                originalAvatarId: user.avatarId,
            });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
